import Breadcrumb from "antd/es/breadcrumb";
import Layout from "antd/es/layout";
import { Content, Footer, Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import Menu, { MenuProps } from "antd/es/menu";
import { useEffect, useRef, useState } from "react";
import Visible from "../../components/Visible";
import ListItems from "./components/ListItems";
import AdminOrders from "./components/AdminOrders";
import { useAuth } from "../../context/AuthContext";
import SaleInfor from "./components/SaleInfor";
import JwtUtils from "../../../applications/utils/JwtUtils";
import {
  ADMINISTRATOR_ID,
  LOCAL_STORAGE_KEY_AUTH,
  ORDER_STATUS_ENUM,
  PRIVILEGE_ROLE_ENUM,
} from "../../../applications/constants/AppConst";
import { useNavigate } from "react-router-dom";
import usePageState from "../../hooks/usePageState";
import Revenue from "./components/Revenue";
import Report from "./components/Report";
import HomeOutlined from "@ant-design/icons/lib/icons/HomeOutlined";
import ContainerOutlined from "@ant-design/icons/lib/icons/ContainerOutlined";
import DollarOutlined from "@ant-design/icons/lib/icons/DollarOutlined";
import LineChartOutlined from "@ant-design/icons/lib/icons/LineChartOutlined";
import ProductOutlined from "@ant-design/icons/lib/icons/ProductOutlined";
import LikeOutlined from "@ant-design/icons/lib/icons/LikeOutlined";
import ClockCircleOutlined from "@ant-design/icons/lib/icons/ClockCircleOutlined";
import CoffeeOutlined from "@ant-design/icons/lib/icons/CoffeeOutlined";
import FrownOutlined from "@ant-design/icons/lib/icons/FrownOutlined";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import CheckSquareOutlined from "@ant-design/icons/lib/icons/CheckSquareOutlined";
import LogoutOutlined from "@ant-design/icons/lib/icons/LogoutOutlined";
import AdminSocketNewOrder from "../../../infrastructure/socket/AdminSocketNewOrder";
import {
  ShoppingCartOutlined,
  TagOutlined,
  UserOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  DiscordOutlined,
  DeleteOutlined,
  ClusterOutlined,
  FireOutlined,
  AppstoreOutlined,
  CalculatorOutlined,
  VerticalAlignBottomOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import InStoreOrder from "./components/InStoreOrder";
import { Avatar, Dropdown, Space, Tooltip } from "antd";
import "./styles/AdminPage.scss";
import Toppings from "./components/Toppings";
import Vouchers from "./components/Vouchers";
import TopSaleCup from "./components/TopSaleCup";
import ListUser from "./components/ListUser";
import UnitManager from "./components/InventoryManager/Uom";
import { getListUoMUseCase } from "../../../applications/useCase/ApiUseCase/UomApiUseCase";
import { useGlobalState } from "../../context/AppGlobalContext";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import RawMaterialManager from "./components/InventoryManager/RawMaterial";
import ImportInventoryManager from "./components/InventoryManager/ImportInventory";
import OperatingCostManager from "./components/InventoryManager/OperatingCost";
import InventoryAudit from "./components/InventoryManager/InventoryAudit";
import ProfitLossDashboard from "./components/InventoryManager/ProfitLossDashboard";
import GuideProfitLoss from "./components/InventoryManager/GuideProfitLoss";

function AdminPages() {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [menuItemSelected, setMenuItemSelected] = useState<string>("pending_confirm_order");
  const [updateOrderId, setUpdateOrderId] = useState<number>();
  const navigator = useNavigate();
  const { showClientError, showError } = usePageState();
  const { authData, setAuthData } = useAuth();
  const [isCustomerView, setIsCustomerView] = useState<boolean>(false);
  const { updateListUoM } = useGlobalState();
  const hasFetchedRef = useRef<boolean>(false);

  async function getListUom() {
    const dataRes = await getListUoMUseCase({
      showError,
    });
    updateListUoM(dataRes);
  }

  useAsyncEffect(async () => {
    if (authData?.access_token) {
      const expirationTime = JwtUtils.decode(authData.access_token)?.exp;
      const currentTime = Number((new Date().getTime() / 1000).toFixed());
      if (!expirationTime || (expirationTime && currentTime > expirationTime)) {
        setAuthData(null);
        localStorage.removeItem(LOCAL_STORAGE_KEY_AUTH);
        navigator("/login");
        window.location.reload();
        showClientError("Phiên đăng nhập hết hạn, vui lòng đăng nhập lại");
        return;
      }
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    await Promise.all([getListUom()]);
  }, [authData]);

  const showAdministratorMenu =
    authData?.id === ADMINISTRATOR_ID && authData.role === PRIVILEGE_ROLE_ENUM.ADMINISTRATOR;

  const items: MenuProps["items"] = [
    {
      key: "user_page",
      label: "Trang User",
      icon: <HomeOutlined />,
    },
    {
      key: "order",
      label: "Bán hàng",
      icon: <ShoppingCartOutlined />,
    },
    {
      key: "admin_orders",
      label: "Danh sách đơn hàng",
      icon: <ContainerOutlined />,
      children: [
        {
          key: "pending_confirm_order",
          label: "Chờ xác nhận",
          icon: <ClockCircleOutlined />,
        },
        {
          key: "in_preparation",
          label: "Đơn đã xác nhận",
          icon: <CoffeeOutlined />,
        },
        {
          key: "complete_order",
          label: "Đơn hoàn thành",
          icon: <CheckSquareOutlined />,
        },
        {
          key: "cancel_order",
          label: "Đơn hủy",
          icon: <FrownOutlined />,
        },
        {
          key: "return_order",
          label: "Đơn hoàn hàng",
          icon: <UndoOutlined />,
        },
        {
          key: "delete_order",
          label: "Đơn đã xóa",
          icon: <DeleteOutlined />,
        },
      ],
    },
    // --- PHẦN THÊM MỚI: QUẢN LÝ KHO & CHI PHÍ ---
    showAdministratorMenu
      ? {
          key: "inventory_management",
          label: "Quản lý kho hàng",
          icon: <AppstoreOutlined />, // Hoặc DatabaseOutlined
          children: [
            {
              key: "profit_loss",
              label: "Báo cáo Lãi/ Lỗ",
              icon: <LineChartOutlined />, //  Báo cáo lãi lỗ theo dữ liệu
            },
            {
              key: "inventory_audit",
              label: "Kiểm kê kho",
              icon: <HistoryOutlined />, // Đối soát tồn thực tế và tồn hệ thống
            },
            {
              key: "operating_cost",
              label: "Chi phí vận hành",
              icon: <DollarOutlined />, // Ghi nhận chi phí nhập hàng
            },
            {
              key: "inventory_import",
              label: "Nhập kho (Đầu vào)",
              icon: <VerticalAlignBottomOutlined />, // Ghi nhận chi phí nhập hàng
            },
            {
              key: "raw_materials",
              label: "Nguyên vật liệu",
              icon: <CalculatorOutlined />, // Quản lý danh mục Trà, Sữa, Trân châu...
            },
            {
              key: "uom",
              label: "Đơn vị tính",
              icon: <CalculatorOutlined />, // Quản lý danh mục Trà, Sữa, Trân châu...
            },
            {
              key: "guide_report",
              label: "Hướng dẫn",
              icon: <InfoCircleOutlined />, // Quản lý danh mục Trà, Sữa, Trân châu...
            },
          ],
        }
      : null,

    // showAdministratorMenu
    //   ? {
    //       key: "production_recipe",
    //       label: "Định mức & Công thức",
    //       icon: <SolutionOutlined />,
    //       children: [
    //         {
    //           key: "recipe_setup",
    //           label: "Cài đặt định mức",
    //           icon: <EditOutlined />, // Cấu hình 1 ly trà sữa tốn bao nhiêu nguyên liệu
    //         },
    //         {
    //           key: "cost_analysis",
    //           label: "Phân tích giá vốn",
    //           icon: <FallOutlined />, // Tính toán chi phí đầu ra dựa trên lượng bán
    //         },
    //       ],
    //     }
    //   : null,
    // // ------------------------------------------
    showAdministratorMenu
      ? {
          key: "statistics",
          label: "Thống kê",
          icon: <LineChartOutlined />,
          children: [
            {
              key: "revenue",
              label: "Doanh thu",
              icon: <DollarOutlined />,
            },
            {
              key: "hot_sale_cup",
              label: "Top bán chạy",
              icon: <FireOutlined />,
            },
          ],
        }
      : null,

    showAdministratorMenu
      ? {
          key: "product_management",
          label: "Quản lý mặt hàng",
          icon: <ProductOutlined />,
          children: [
            {
              key: "items",
              label: "Sản phẩm",
              icon: <ProductOutlined />,
            },
            {
              key: "toppings",
              label: "Topping",
              icon: <TagOutlined />,
            },
          ],
        }
      : null,

    // showAdministratorMenu
    //   ? {
    //       key: "user_management",
    //       label: "Quản lý người dùng",
    //       icon: <ClusterOutlined />,
    //       children: [
    //         {
    //           key: "users",
    //           label: "Thành viên",
    //           icon: <ClusterOutlined />,
    //         },
    //       ],
    //     }
    //   : null,

    showAdministratorMenu
      ? {
          key: "marketing",
          label: "Marketing",
          icon: <LikeOutlined />,
          children: [
            {
              key: "sales",
              label: "Khuyến mãi",
              icon: <LikeOutlined />,
            },
            {
              key: "vouchers",
              label: "Vouchers",
              icon: <DiscordOutlined />,
            },
          ],
        }
      : null,
  ].filter(Boolean);

  const redirectToPending = () => {
    setIsCustomerView(false);
    setMenuItemSelected("pending_confirm_order");
  };

  const handleLogout = () => {
    setAuthData(null);
    localStorage.removeItem(LOCAL_STORAGE_KEY_AUTH);
    window.location.reload();
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "sign_out",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  useEffect(() => {
    setUpdateOrderId(undefined);
  }, [menuItemSelected]);

  return (
    <>
      {isCustomerView ? (
        <Layout style={{ height: "100vh" }}>
          <Header
            style={{
              justifyContent: "flex-end",
            }}
            className="header"
          >
            <Tooltip title="Thoát chế độ khách hàng" placement="left">
              <FullscreenExitOutlined
                onClick={() => setIsCustomerView(false)}
                className="fullscreen-toggle-icon"
              />
            </Tooltip>
          </Header>
          <Content style={{ margin: "0 16px" }}>
            <InStoreOrder redirectToPending={redirectToPending} />
          </Content>
        </Layout>
      ) : (
        <Layout style={{ minHeight: "100vh", overflow: "hidden" }}>
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            style={{
              height: "100vh",
              overflow: "auto", // để sider scroll riêng
            }}
          >
            <div className="demo-logo-vertical" />
            <Menu
              theme="dark"
              selectedKeys={[menuItemSelected]}
              defaultOpenKeys={["admin_orders"]}
              onSelect={(e) => {
                if (e.key === "user_page") {
                  setAuthData({ ...authData!, use_admin_site: false });
                  navigator("/", { replace: true });
                } else {
                  setMenuItemSelected(e.key);
                }
              }}
              mode="inline"
              items={items}
            />
          </Sider>
          <Layout style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <Header
              style={{
                justifyContent: "space-between",
                display: "flex",
                alignItems: "center",
                background: "#fff",
                padding: "0 16px",
                flexShrink: 0,
              }}
              className="header"
            >
              <Breadcrumb
                style={{ padding: "12px" }}
                items={[
                  { title: "Trang chủ" },
                  { title: (items?.find((e) => e?.key === menuItemSelected) as any)?.label },
                ]}
              />

              <div style={{ padding: "0px 12px" }} className="header-right">
                <Tooltip title="Hiển thị chế độ khách hàng">
                  <FullscreenOutlined
                    className="fullscreen-toggle-icon"
                    onClick={() => setIsCustomerView(true)}
                  />
                </Tooltip>
                <Dropdown
                  menu={{ items: userMenuItems }}
                  trigger={["click"]}
                  placement="bottomRight"
                  className="user-dropdown-trigger"
                >
                  <Space style={{ cursor: "pointer", height: "48px" }}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span className="user-name">{authData?.display_name}</span>
                  </Space>
                </Dropdown>
              </div>
            </Header>
            <Content className="main-content-page">
              <Visible isVisible={menuItemSelected === "items" && showAdministratorMenu}>
                <ListItems />
              </Visible>
              <Visible isVisible={menuItemSelected === "toppings" && showAdministratorMenu}>
                <Toppings />
              </Visible>
              <Visible isVisible={menuItemSelected === "order"}>
                <InStoreOrder redirectToPending={redirectToPending} updateOrderId={updateOrderId} />
              </Visible>
              <Visible isVisible={menuItemSelected === "pending_confirm_order"}>
                <AdminOrders
                  setMenuItemSelected={setMenuItemSelected}
                  setUpdateOrderId={setUpdateOrderId}
                  status={ORDER_STATUS_ENUM.NOT_CONFIRM}
                />
              </Visible>
              <Visible isVisible={menuItemSelected === "in_preparation"}>
                <AdminOrders
                  setMenuItemSelected={setMenuItemSelected}
                  setUpdateOrderId={setUpdateOrderId}
                  status={ORDER_STATUS_ENUM.APPROVE}
                />
              </Visible>
              <Visible isVisible={menuItemSelected === "complete_order"}>
                <AdminOrders
                  setMenuItemSelected={setMenuItemSelected}
                  setUpdateOrderId={setUpdateOrderId}
                  status={ORDER_STATUS_ENUM.COMPLETE}
                />
              </Visible>
              <Visible isVisible={menuItemSelected === "cancel_order"}>
                <AdminOrders status={ORDER_STATUS_ENUM.REJECT} />
              </Visible>
              <Visible isVisible={menuItemSelected === "return_order"}>
                <AdminOrders status={ORDER_STATUS_ENUM.RETURN} />
              </Visible>
              <Visible isVisible={menuItemSelected === "delete_order"}>
                <AdminOrders status={ORDER_STATUS_ENUM.DELETE} />
              </Visible>
              <Visible isVisible={showAdministratorMenu}>
                <Visible isVisible={menuItemSelected === "revenue"}>
                  <Revenue />
                </Visible>
                <Visible isVisible={menuItemSelected === "users"}>
                  <ListUser />
                </Visible>
                <Visible isVisible={menuItemSelected === "report"}>
                  <Report />
                </Visible>
                <Visible isVisible={menuItemSelected === "hot_sale_cup"}>
                  <TopSaleCup />
                </Visible>
                <Visible isVisible={menuItemSelected === "sales"}>
                  <SaleInfor />
                </Visible>
                <Visible isVisible={menuItemSelected === "vouchers"}>
                  <Vouchers />
                </Visible>
                <Visible isVisible={menuItemSelected === "uom"}>
                  <UnitManager />
                </Visible>
                <Visible isVisible={menuItemSelected === "raw_materials"}>
                  <RawMaterialManager />
                </Visible>
                <Visible isVisible={"inventory_import" === menuItemSelected}>
                  <ImportInventoryManager />
                </Visible>
                <Visible isVisible={menuItemSelected === "operating_cost"}>
                  <OperatingCostManager />
                </Visible>
                <Visible isVisible={menuItemSelected === "inventory_audit"}>
                  <InventoryAudit />
                </Visible>
                <Visible isVisible={menuItemSelected === "profit_loss"}>
                  <ProfitLossDashboard />
                </Visible>
                <Visible isVisible={menuItemSelected === "guide_report"}>
                  <GuideProfitLoss setMenuItemSelected={setMenuItemSelected} />
                </Visible>
              </Visible>
            </Content>
            <Footer style={{ textAlign: "center", padding: "8px", flexShrink: 0 }}>
              © 2025 Louis
            </Footer>
          </Layout>
        </Layout>
      )}
      <AdminSocketNewOrder />
    </>
  );
}
export default AdminPages;
